/* THE RUN LOG, AS THE CONSOLE HAS IT. The run analysis the customer copy carries names, by bay and by case minute, the
   harms (with the minute they were recorded), the costly windows (a stretch of minutes in which the Resident's choice cost
   something, with the tools it used) and which cases ended in a death. That is the whole log; nothing else is invented.
   The section of the ward at minute m is this log read at m: a bed is thin inside a costly window, breaks at a harm
   (and stays broken while the harm persisted at the end), and a death stills it. Minutes are case minutes: cases played in
   the same bay share the bay's strata and the gravest state wins. */
import type { Pattern } from '../licensee/model/theatre.ts'
import { PROBLEMS, type ProblemKey } from '../licensee/model/problems.ts'
import { RANK } from './geometry.ts'

export const SYSTEMS: readonly ProblemKey[] = PROBLEMS.map((p) => p.key)

export type LogCase = { case_ref: string; bay: number; died: boolean }
export type LogHarm = { case_ref: string; bay: number; kind: string; minute: number | null; persistent_at_end: boolean }
export type LogWindow = { case_ref: string; bay: number; from_min: number; to_min: number; tools: string[] }
export type LogInput = { cases: readonly LogCase[]; harms: readonly LogHarm[]; windows: readonly LogWindow[] }

export type LogEvent =
  | { kind: 'harm'; bay: number; minute: number; system: ProblemKey | null; persists: boolean }
  | { kind: 'window'; bay: number; from: number; to: number; tools: string[] }
  | { kind: 'death'; bay: number; minute: number }

export type RunLog = { count: number; horizon: number; step: number; steps: number; events: LogEvent[] }

const HARM_WORDS: [ProblemKey, RegExp][] = [
  ['circulation', /hypotens|shock|perfus|arrhythm|tachy|brady|bleed|pressor|vasopress/i],
  ['oxygenation', /hypox|desat|spo2|oxygen/i],
  ['ventilation', /respir|ventilat|airway|apnea|apnoea|intubat/i],
  ['temperature', /fever|hyperther|hypother|temperat/i],
  ['consciousness', /sedat|delir|confus|conscious|neuro|coma|agitat/i],
]

/** The system a harm names, from its kind; null when it names none (the whole patient is strained). */
export function systemOfHarm(kind: string): ProblemKey | null {
  return HARM_WORDS.find(([, re]) => re.test(kind))?.[0] ?? null
}

const STEPS = [5, 10, 15, 30, 60, 120, 240]
/** The stratum height in minutes: the smallest step that keeps the ward within 24 strata. */
export function stepFor(horizon: number): number {
  return STEPS.find((s) => Math.ceil(horizon / s) <= 24) ?? 480
}

export function runLogOf(count: number, input: LogInput): RunLog {
  const events: LogEvent[] = []
  const last = new Map<string, number>()
  const seen = (ref: string, m: number) => last.set(ref, Math.max(last.get(ref) ?? 0, m))
  let horizon = 60
  for (const h of input.harms) {
    if (h.bay < 1 || h.bay > count) continue
    const minute = h.minute ?? 0
    events.push({ kind: 'harm', bay: h.bay, minute, system: systemOfHarm(h.kind), persists: h.persistent_at_end })
    seen(h.case_ref, minute)
    horizon = Math.max(horizon, minute)
  }
  for (const w of input.windows) {
    if (w.bay < 1 || w.bay > count) continue
    events.push({ kind: 'window', bay: w.bay, from: w.from_min, to: w.to_min, tools: [...w.tools] })
    seen(w.case_ref, w.to_min)
    horizon = Math.max(horizon, w.to_min)
  }
  const step = stepFor(horizon)
  for (const c of input.cases) {
    if (!c.died || c.bay < 1 || c.bay > count) continue
    events.push({ kind: 'death', bay: c.bay, minute: Math.max(last.get(c.case_ref) ?? 0, step) })
  }
  return { count, horizon, step, steps: Math.max(1, Math.ceil(horizon / step)), events }
}

export type BedState = { systems: Pattern[]; dead: boolean }

/** The state of bay `bay` at `minute`, read from the log. */
export function stateAt(log: RunLog, bay: number, minute: number): BedState {
  const systems: Pattern[] = SYSTEMS.map(() => 'ok')
  const raise = (i: number, p: Pattern) => {
    if (RANK[p] > RANK[systems[i]]) systems[i] = p
  }
  let dead = false
  for (const e of log.events) {
    if (e.bay !== bay) continue
    if (e.kind === 'window' && e.from <= minute && minute <= e.to) SYSTEMS.forEach((_, i) => raise(i, 'degrading'))
    else if (e.kind === 'harm' && e.minute <= minute) {
      const failing = e.persists || minute < e.minute + log.step
      const hit = e.system === null ? [0] : [SYSTEMS.indexOf(e.system)]
      if (e.system === null) SYSTEMS.forEach((_, i) => raise(i, 'degrading'))
      for (const i of hit) raise(i, failing ? 'failing' : 'degrading')
    } else if (e.kind === 'death' && e.minute <= minute) dead = true
  }
  return { systems, dead }
}

/** The ward cut at stratum k: every bed's state at that stratum's minute. */
export function sectionAt(log: RunLog, k: number): { minute: number; beds: BedState[] } {
  const minute = Math.min(Math.max(0, k), log.steps) * log.step
  return { minute, beds: Array.from({ length: log.count }, (_, i) => stateAt(log, i + 1, minute)) }
}
